import React, { useEffect, useState } from "react";
import { api, formatMXN } from "../lib/api";
import { useConfirm } from "../context/ConfirmContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose } from "./ui/drawer";
import { Plus, Users, HandCoins, Eye, CheckCircle2, Trash2, FileText, Banknote, Trophy, Network, Search, Edit3, Download, AlertCircle, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PromoterRanking from "./PromoterRanking";
import AdminTeams from "./AdminTeams";

function monthLabel(s) {
  if (!s) return "—";
  const [y, m] = s.split("-");
  const names = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const initialPromoter = { name: "", phone: "", address: "", password: "", commission_per_payment: 50 };

export default function PromotersAdmin() {
  return (
    <Tabs defaultValue="list">
      <TabsList className="bg-white border border-border" data-testid="promoters-subtabs">
        <TabsTrigger value="list" data-testid="prom-subtab-list"><Users className="w-3.5 h-3.5 mr-1.5" />Promotores</TabsTrigger>
        <TabsTrigger value="payments" data-testid="prom-subtab-payments"><FileText className="w-3.5 h-3.5 mr-1.5" />Pagos del mes</TabsTrigger>
        <TabsTrigger value="commissions" data-testid="prom-subtab-commissions"><HandCoins className="w-3.5 h-3.5 mr-1.5" />Comisiones a pagar</TabsTrigger>
        <TabsTrigger value="teams" data-testid="prom-subtab-teams"><Network className="w-3.5 h-3.5 mr-1.5" />Equipos</TabsTrigger>
        <TabsTrigger value="ranking" data-testid="prom-subtab-ranking"><Trophy className="w-3.5 h-3.5 mr-1.5" />Ranking</TabsTrigger>
      </TabsList>

      <TabsContent value="list" className="mt-6"><PromotersList /></TabsContent>
      <TabsContent value="payments" className="mt-6"><PaymentsReport /></TabsContent>
      <TabsContent value="commissions" className="mt-6"><CommissionsReport /></TabsContent>
      <TabsContent value="teams" className="mt-6"><AdminTeams /></TabsContent>
      <TabsContent value="ranking" className="mt-6"><PromoterRanking mode="admin" /></TabsContent>
    </Tabs>
  );
}

function PromotersList() {
  const confirm = useConfirm();
  const { impersonate } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(initialPromoter);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewingBiz, setViewingBiz] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editBusy, setEditBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/promoters${q ? `?search=${encodeURIComponent(q)}` : ""}`);
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/promoters", form);
      toast.success("Promotor creado");
      setCreating(false); setForm(initialPromoter);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!await confirm(`¿Eliminar a "${p.name}"? Sus comercios afiliados quedarán sin promotor.`)) return;
    try {
      await api.delete(`/admin/promoters/${p.id}`);
      toast.success("Promotor eliminado");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const openView = async (p) => {
    setViewing(p);
    const { data } = await api.get(`/admin/promoters/${p.id}/businesses`);
    setViewingBiz(data || []);
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditForm({
      name: p.name || "",
      phone: p.phone || "",
      address: p.address || "",
      commission_per_payment: p.commission_per_payment || 50,
      password: "",
    });
  };

  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const payload = {};
      const orig = editing;
      for (const [k, v] of Object.entries(editForm)) {
        if (k === "password" && v) { payload.password = v; continue; }
        const origVal = k === "commission_per_payment" ? (orig[k] || 50) : (orig[k] || "");
        if (v !== origVal) payload[k] = v;
      }
      if (Object.keys(payload).length === 0) {
        toast.error("Sin cambios");
        setEditBusy(false);
        return;
      }
      await api.put(`/admin/promoters/${editing.id}`, payload);
      toast.success("Promotor actualizado");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setEditBusy(false); }
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h3 className="font-display text-2xl tracking-tight">Promotores</h3>
          <p className="text-sm text-muted-foreground mt-1">Alta, baja y consulta de promotores afiliados al sistema OFERTAS.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o teléfono" data-testid="prom-search" />
          </div>
          <Button onClick={load} size="sm" data-testid="prom-search-btn">Buscar</Button>
          <Dialog open={creating} onOpenChange={setCreating}>
            <DialogTrigger asChild>
              <Button className="btn-primary rounded-full" data-testid="prom-create-btn"><Plus className="w-4 h-4 mr-1.5" />Nuevo promotor</Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg" data-testid="prom-create-dialog">
            <DialogHeader><DialogTitle>Dar de alta a un promotor</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label className="text-sm">Nombre *</Label>
                <Input required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="prom-create-name" />
              </div>
              <div>
                <Label className="text-sm">Teléfono *</Label>
                <Input required value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value.replace(/\D/g, "").slice(0, 12)})} placeholder="5512345678" inputMode="numeric" data-testid="prom-create-phone" />
              </div>
              <div>
                <Label className="text-sm">Domicilio *</Label>
                <Input required value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} data-testid="prom-create-address" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm">Contraseña inicial *</Label>
                  <Input required type="text" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} placeholder="Ej. promo123" data-testid="prom-create-password" />
                </div>
                <div>
                  <Label className="text-sm">Comisión por pago (MXN)</Label>
                  <Input type="number" value={form.commission_per_payment} onChange={(e) => setForm({...form, commission_per_payment: parseFloat(e.target.value) || 50})} data-testid="prom-create-commission" />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setCreating(false)}>Cancelar</Button>
                <Button type="submit" disabled={busy} className="btn-primary" data-testid="prom-create-submit">{busy ? "Creando…" : "Crear"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? <div className="text-muted-foreground">Cargando…</div> : items.length === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center border-2 border-dashed border-border rounded-xl" data-testid="prom-empty">
          Aún no hay promotores. Crea el primero con el botón "Nuevo promotor".
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground border-b border-border">
              <tr><th className="py-2">Promotor</th><th>Teléfono</th><th>Comercios</th><th>Ganado total</th><th>Por cobrar</th><th>Estado</th><th></th></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-border" data-testid={`prom-row-${p.id}`}>
                  <td className="py-3">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.address}</div>
                  </td>
                  <td className="font-mono text-xs">{p.phone}</td>
                  <td>
                    <Badge variant="outline">{p.stats?.active_businesses || 0}/{p.stats?.total_businesses || 0}</Badge>
                    <div className="text-[10px] text-muted-foreground mt-0.5">activos / total</div>
                  </td>
                  <td className="font-medium">{formatMXN(p.stats?.total_earned || 0)}</td>
                  <td className="text-amber-700 font-medium">{formatMXN(p.stats?.pending_amount || 0)}</td>
                  <td>{p.is_active ? <Badge className="bg-[#7C3AED] text-white">Activo</Badge> : <Badge variant="destructive">Inactivo</Badge>}</td>
                  <td>
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        title="Entrar como este promotor"
                        onClick={async () => {
                          try { await impersonate("promoter", p.id); }
                          catch (e) { toast.error(e.response?.data?.detail || "No se pudo entrar a la cuenta"); }
                        }}
                        data-testid={`prom-impersonate-${p.id}`}
                      ><LogIn className="w-3.5 h-3.5" /></Button>
                      <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`prom-edit-${p.id}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                      <Button variant="outline" size="sm" onClick={() => openView(p)} data-testid={`prom-view-${p.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                      <Button variant="outline" size="sm" onClick={() => remove(p)} className="text-rose-600 hover:bg-rose-50" data-testid={`prom-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl" data-testid="prom-view-dialog">
          <DialogHeader><DialogTitle>{viewing?.name} — Comercios afiliados</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">{viewing.phone} · {viewing.address}</div>
              {viewingBiz.length === 0 ? (
                <div className="text-sm text-muted-foreground py-6 text-center">Este promotor todavía no afilia comercios.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-muted-foreground border-b border-border">
                    <tr><th className="py-2">Comercio</th><th>CP</th><th>Estado</th><th>Vence</th></tr>
                  </thead>
                  <tbody>
                    {viewingBiz.map((b) => (
                      <tr key={b.id} className="border-b border-border">
                        <td className="py-2">{b.business_name}<div className="text-xs text-muted-foreground">{b.giro}</div></td>
                        <td>{b.postal_code}</td>
                        <td>{b.subscription_active ? <Badge className="bg-[#7C3AED] text-white">Activo</Badge> : <Badge variant="outline">Inactivo</Badge>}</td>
                        <td className="text-xs">{b.subscription_expires_at ? new Date(b.subscription_expires_at).toLocaleDateString("es-MX") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Drawer open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DrawerContent className="max-h-[85vh]" data-testid="prom-edit-drawer">
          <DrawerHeader>
            <DrawerTitle>Editar promotor</DrawerTitle>
          </DrawerHeader>
          {editing && (
            <div className="px-4 pb-6 space-y-4 overflow-y-auto">
              <div>
                <Label className="text-sm">Nombre</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="prom-edit-name" />
              </div>
              <div>
                <Label className="text-sm">Teléfono</Label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value.replace(/\D/g, "").slice(0, 12) })} inputMode="numeric" data-testid="prom-edit-phone" />
              </div>
              <div>
                <Label className="text-sm">Domicilio</Label>
                <Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} data-testid="prom-edit-address" />
              </div>
              <div>
                <Label className="text-sm">Comisión por pago (MXN)</Label>
                <Input type="number" value={editForm.commission_per_payment} onChange={(e) => setEditForm({ ...editForm, commission_per_payment: parseFloat(e.target.value) || 50 })} data-testid="prom-edit-commission" />
              </div>
              <div>
                <Label className="text-sm">Nueva contraseña <span className="text-muted-foreground font-normal">(dejar vacío para no cambiar)</span></Label>
                <Input type="text" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Nueva contraseña" data-testid="prom-edit-password" />
              </div>
            </div>
          )}
          <DrawerFooter>
            <div className="flex gap-2 justify-end">
              <DrawerClose asChild>
                <Button variant="outline">Cancelar</Button>
              </DrawerClose>
              <Button className="btn-primary" onClick={saveEdit} disabled={editBusy} data-testid="prom-edit-save">
                {editBusy ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function PaymentsReport() {
  const [month, setMonth] = useState(currentMonthKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reports/payments-by-month?month=${m}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month]);

  return (
    <div className="bg-white border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h3 className="font-display text-2xl tracking-tight">Reporte de pagos mensuales</h3>
          <p className="text-sm text-muted-foreground mt-1">Comercios que pagaron su cuota de $199 en el mes seleccionado, con su promotor asociado.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">Mes:</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="report-month-input" />
        </div>
      </div>

      {loading || !data ? (
        <div className="text-muted-foreground">Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
            <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-xl p-4">
              <div className="overline text-xs text-muted-foreground">Pagos del mes</div>
              <div className="font-display text-3xl mt-1">{data.total_payments}</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="overline text-xs text-muted-foreground">Ingresos del mes</div>
              <div className="font-display text-3xl mt-1 text-emerald-700">{formatMXN(data.total_revenue)}</div>
            </div>
            <div className="bg-white border border-border rounded-xl p-4">
              <div className="overline text-xs text-muted-foreground">Mes</div>
              <div className="font-display text-3xl mt-1">{monthLabel(data.month)}</div>
            </div>
          </div>

          {data.rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center border-2 border-dashed border-border rounded-xl" data-testid="report-empty">
              Sin pagos en {monthLabel(data.month)}.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payments-report-table">
                <thead className="text-left text-muted-foreground border-b border-border">
                  <tr><th className="py-2">Comercio</th><th>CP</th><th>WhatsApp</th><th>Monto</th><th>Promotor</th><th>Fecha pago</th></tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.payment_id} className="border-b border-border" data-testid={`payreport-row-${r.payment_id}`}>
                      <td className="py-3 font-medium">{r.business_name}</td>
                      <td>{r.postal_code}</td>
                      <td className="font-mono text-xs">{r.whatsapp}</td>
                      <td className="font-medium">{formatMXN(r.amount)}</td>
                      <td>
                        {r.promoter_name ? (
                          <Badge variant="outline" className="text-[#7C3AED] border-[#C4B5FD]">{r.promoter_name}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">sin promotor</span>
                        )}
                      </td>
                      <td className="text-xs">{r.paid_at ? new Date(r.paid_at).toLocaleDateString("es-MX") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CommissionsReport() {
  const confirm = useConfirm();
  const [month, setMonth] = useState(currentMonthKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reports/commissions?month=${m}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month]);

  const markPaid = async (cid) => {
    try {
      await api.post(`/admin/commissions/${cid}/mark-paid`);
      toast.success("Comisión marcada como pagada");
      load(month);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const markPaidBulk = async (pid, name) => {
    if (!await confirm(`¿Marcar todas las comisiones pendientes de ${name} en ${monthLabel(month)} como pagadas?`)) return;
    try {
      const { data } = await api.post(`/admin/commissions/mark-paid-bulk?promoter_id=${pid}&month=${month}`);
      toast.success(`${data.updated} comisión(es) marcada(s) como pagada(s)`);
      load(month);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const exportCSV = () => {
    if (!data?.promoters?.length) return;
    const rows = [
      ["Promotor", "Teléfono", "Tiene cuenta bancaria", "Banco", "CLABE / Cuenta", "Titular", "Total", "Por pagar", "Ya pagado", "# Comisiones"],
    ];
    data.promoters.forEach((p) => {
      const acc = p.payment_account;
      rows.push([
        p.promoter_name,
        p.promoter_phone || "",
        acc ? "Sí" : "No",
        acc?.bank_name || "",
        acc?.account_number || "",
        acc?.account_holder || "",
        p.total_amount,
        p.pending_amount,
        p.paid_amount,
        p.commissions_count,
      ]);
    });
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `comisiones-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div>
          <h3 className="font-display text-2xl tracking-tight">Comisiones a pagar a promotores</h3>
          <p className="text-sm text-muted-foreground mt-1">Por cada $199 que un comercio paga, su promotor gana $100. Aquí ves a quién y cuánto debes pagar este mes.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-xs text-muted-foreground">Mes:</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="commissions-month-input" />
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={!data?.promoters?.length} className="rounded-full" data-testid="commissions-export-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" />Exportar Excel
          </Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="text-muted-foreground">Cargando…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="bg-white border border-border rounded-xl p-4">
              <div className="overline text-xs text-muted-foreground">Promotores con comisión</div>
              <div className="font-display text-3xl mt-1">{data.total_promoters}</div>
            </div>
            <div className="bg-[#F5F3FF] border border-[#DDD6FE] rounded-xl p-4">
              <div className="overline text-xs text-muted-foreground">Total del mes</div>
              <div className="font-display text-3xl mt-1 text-[#5B21B6]">{formatMXN(data.grand_total)}</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="overline text-xs text-muted-foreground">Por pagar</div>
              <div className="font-display text-3xl mt-1 text-amber-800">{formatMXN(data.grand_pending)}</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="overline text-xs text-muted-foreground">Ya pagado</div>
              <div className="font-display text-3xl mt-1 text-emerald-700">{formatMXN(data.grand_paid)}</div>
            </div>
          </div>

          {data.promoters.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center border-2 border-dashed border-border rounded-xl" data-testid="commissions-empty">
              Sin comisiones en {monthLabel(data.month)}. Aparecen cuando un comercio con promotor paga su cuota.
            </div>
          ) : (
            <div className="space-y-4" data-testid="commissions-report">
              {data.promoters.map((p) => (
                <div key={p.promoter_id} className="border border-border rounded-xl overflow-hidden" data-testid={`comm-promoter-${p.promoter_id}`}>
                  <div className="bg-[#FAFAFA] px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{p.promoter_name}</span>
                        {p.payment_account ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" title={`${p.payment_account.bank_name} · ${p.payment_account.account_number}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" />Con cuenta
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-red-500 border-red-200 text-[10px]">
                            <AlertCircle className="w-3 h-3 mr-1" />Sin cuenta
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">{p.promoter_phone}</div>
                      {p.payment_account && (
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                          {p.payment_account.bank_name} · <span className="font-mono">{p.payment_account.account_number}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-[10px] text-muted-foreground">{p.commissions_count} comisión(es)</div>
                        <div className="font-display text-xl">{formatMXN(p.total_amount)}</div>
                      </div>
                      {p.pending_amount > 0 && (
                        <Button size="sm" className="btn-primary rounded-full" onClick={() => markPaidBulk(p.promoter_id, p.promoter_name)} data-testid={`comm-bulkpay-${p.promoter_id}`}>
                          <Banknote className="w-3.5 h-3.5 mr-1" />Pagar {formatMXN(p.pending_amount)}
                        </Button>
                      )}
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {p.details.map((c) => (
                        <tr key={c.id} className="border-t border-border" data-testid={`comm-detail-${c.id}`}>
                          <td className="py-2 px-4 text-zinc-700">{c.business_name}</td>
                          <td className="py-2 px-4 font-medium">{formatMXN(c.amount)}</td>
                          <td className="py-2 px-4 text-xs">{new Date(c.created_at).toLocaleDateString("es-MX")}</td>
                          <td className="py-2 px-4">
                            {c.status === "paid" ? (
                              <Badge className="bg-emerald-500 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Pagado</Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-700 border-amber-300">Pendiente</Badge>
                            )}
                          </td>
                          <td className="py-2 px-4 text-right">
                            {c.status === "pending" && (
                              <Button variant="outline" size="sm" onClick={() => markPaid(c.id)} data-testid={`comm-markpaid-${c.id}`}>
                                Marcar pagado
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
